/**
 * Risk Level Badge Component
 *
 * Displays a risk level indicator as a DLS-compliant chip.
 * - Height: 32px
 * - Width: 118px (fixed for consistent alignment)
 * - Border radius: 6px (rounded rectangle)
 * - Colors: Status colors from DLS guidelines
 * - Icons: Level-specific icons from lucide-react
 *
 * Optionally displays a 14-day risk trend bar below the badge.
 */

import { CircleAlert, Activity, CircleCheck, LucideIcon } from "lucide-react";
import { RiskLevel, getRiskLevelColor, getRiskLevelLabel } from "../../types/residents";
import { RiskTrendBar } from "./RiskTrendBar";
import type { RiskLevel as RiskLevelType } from "../../types/residents";

interface RiskLevelBadgeProps {
  level: RiskLevel;
  trend?: (RiskLevelType | null)[];
  className?: string;
}

function getRiskLevelIcon(level: RiskLevel): LucideIcon | null {
  switch (level) {
    case "HIGH":
      return CircleAlert;
    case "MEDIUM":
      return Activity;
    case "LOW":
      return CircleCheck;
    default:
      return null;
  }
}

export function RiskLevelBadge({ level, trend, className = "" }: RiskLevelBadgeProps) {
  const colors = getRiskLevelColor(level);
  const label = getRiskLevelLabel(level);
  const Icon = getRiskLevelIcon(level);

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <span
        className="inline-flex items-center justify-center gap-1.5 h-8 w-[130px] text-xs font-bold rounded-md whitespace-nowrap"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: colors.border ? `1px solid ${colors.border}` : undefined,
        }}
      >
        {Icon && <Icon size={14} strokeWidth={2.5} />}
        {label}
      </span>
      {trend && trend.length > 0 && <RiskTrendBar trend={trend} />}
    </div>
  );
}
